/**
 * usePasskeys Hook
 *
 * React hook for all passkey (WebAuthn) operations including:
 * - Registration (adding new passkeys)
 * - Authentication (logging in with passkeys)
 * - Management (listing and removing passkeys)
 * - Prompt logic (whether to show the "add passkey" prompt)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { getBiometricType, getBiometricDisplayName, checkPasskeySupport, canProbablyUsePasskeys, PasskeyData, BiometricType, UserInfoWithPasskeys, PasskeySupportResult } from "@/lib/passkey-config";
import supabaseClient from "@/utils/supabase-browser";
import { AppSupabaseClient } from "@/types";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { toast } from "sonner";

/**
 * Get passkey data using client-side Supabase
 * Inlined here to avoid importing server-only code from passkey-helpers.ts
 */
async function getUserPasskeysClient(supabase: AppSupabaseClient, userId: string): Promise<PasskeyData | null> {
  const {
    data: profile,
    error
  } = await supabase.from("user_profiles").select("passkeys").eq("id", userId).single();
  if (error || !profile) {
    return null;
  }
  return profile.passkeys as unknown as PasskeyData || null;
}

// Maximum number of times to show the prompt
const MAX_PROMPT_DISMISS_COUNT = 5;
export interface UsePasskeysReturn {
  // Passkey data
  passkeys: PasskeyData | null;
  hasPasskeys: boolean;
  isLoading: boolean;

  // Biometric info
  biometricType: BiometricType;
  biometricDisplayName: string;
  isPasskeySupported: boolean;
  supportDetails: PasskeySupportResult | null;

  // Prompt logic
  shouldShowPrompt: boolean;
  dismissPrompt: () => Promise<void>;

  // Registration
  registerPasskey: (deviceName?: string) => Promise<boolean>;
  isRegistering: boolean;

  // Authentication (for login page)
  authenticateWithPasskey: () => Promise<{
    success: boolean;
    redirectUrl?: string;
  }>;
  isAuthenticating: boolean;

  // Management
  removePasskey: (credentialId: string) => Promise<void>;
  isRemoving: boolean;

  // Errors
  error: Error | null;
}
export function usePasskeys(): UsePasskeysReturn {
  const queryClient = useQueryClient();
  const toastRef = useRef<string | number | null>(null);

  // State - start with null to match server render, update after mount
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [supportDetails, setSupportDetails] = useState<PasskeySupportResult | null>(null);
  const [biometricType, setBiometricType] = useState<BiometricType>("passkey");
  const [error, setError] = useState<Error | null>(null);

  // Get logged in user (may throw if not logged in - that's ok for most uses)
  let userId: string | null = null;
  try {
    const user = useLoggedInUser();
    userId = user?.id || null;
  } catch {
    // User not logged in - that's fine for the login page
  }

  // After mount: immediately set optimistic value, then run full async check
  useEffect(() => {
    // Immediate optimistic check (sync) - shows UI right after hydration
    const optimistic = canProbablyUsePasskeys();
    setIsSupported(optimistic);
    setBiometricType(getBiometricType());

    // Full async check - only updates if platform authenticator unavailable
    const runCheck = async () => {
      const result = await checkPasskeySupport();
      if (result.supported !== optimistic) {
        setIsSupported(result.supported);
      }
      setSupportDetails(result);
    };
    runCheck();
  }, []);

  // Query for user's passkeys
  const {
    data: passkeys,
    isLoading,
    refetch: refetchPasskeys
  } = useQuery({
    queryKey: ["passkeys", userId],
    queryFn: async () => {
      if (!userId) return null;
      return getUserPasskeysClient(supabaseClient, userId);
    },
    enabled: !!userId,
    staleTime: 30000 // 30 seconds
  });
  const hasPasskeys = (passkeys?.credentials?.length ?? 0) > 0;

  // Determine if we should show the prompt
  const shouldShowPrompt = isSupported && !hasPasskeys && !isLoading && !!userId && (passkeys?.promptDismissCount ?? 0) < MAX_PROMPT_DISMISS_COUNT;

  // Dismiss prompt mutation
  const dismissPromptMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/passkeys/dismiss-prompt", {
        method: "POST",
        credentials: "include"
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to dismiss prompt");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["passkeys", userId]
      });
    }
  });

  // Registration mutation
  const registerMutation = useMutation({
    mutationFn: async (deviceName?: string) => {
      // 1. Get registration options from server
      const optionsResponse = await fetch("/api/passkeys/register-options", {
        method: "POST",
        credentials: "include"
      });
      if (!optionsResponse.ok) {
        const data_0 = await optionsResponse.json();
        throw new Error(data_0.error || "Failed to get registration options");
      }
      const options = await optionsResponse.json();

      // 2. Start WebAuthn registration
      const credential = await startRegistration({
        optionsJSON: options
      });

      // 3. Verify with server
      const verifyResponse = await fetch("/api/passkeys/register-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          credential,
          deviceName
        })
      });
      if (!verifyResponse.ok) {
        const data_1 = await verifyResponse.json();
        throw new Error(data_1.error || "Failed to verify registration");
      }
      return verifyResponse.json();
    },
    onMutate: () => {
      toastRef.current = toast.loading(`Setting up ${getBiometricDisplayName(biometricType)}...`);
    },
    onSuccess: () => {
      toast.success(`${getBiometricDisplayName(biometricType)} added successfully!`, {
        id: toastRef.current!
      });
      queryClient.invalidateQueries({
        queryKey: ["passkeys", userId]
      });
    },
    onError: (err: Error) => {
      const message = err.message || "Failed to add passkey";
      // Don't show error for user cancellation
      if (message.includes("cancelled") || message.includes("canceled") || message.includes("abort")) {
        toast.dismiss(toastRef.current!);
      } else {
        toast.error(message, {
          id: toastRef.current!
        });
      }
      setError(err);
    }
  });

  // Authentication mutation (for login)
  const authenticateMutation = useMutation({
    mutationFn: async () => {
      // 1. Get authentication options from server
      const optionsResponse_0 = await fetch("/api/passkeys/login-options", {
        method: "POST",
        credentials: "include"
      });
      if (!optionsResponse_0.ok) {
        const data_2 = await optionsResponse_0.json();
        throw new Error(data_2.error || "Failed to get authentication options");
      }
      const options_0 = await optionsResponse_0.json();

      // 2. Start WebAuthn authentication
      const credential_0 = await startAuthentication({
        optionsJSON: options_0
      });

      // 3. Verify with server
      const verifyResponse_0 = await fetch("/api/passkeys/login-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          credential: credential_0
        })
      });
      if (!verifyResponse_0.ok) {
        const data_3 = await verifyResponse_0.json();
        throw new Error(data_3.error || "Failed to verify authentication");
      }
      return verifyResponse_0.json();
    },
    onMutate: () => {
      toastRef.current = toast.loading("Authenticating...");
    },
    onSuccess: data_4 => {
      toast.success("Login successful!", {
        id: toastRef.current!
      });
      // Redirect will be handled by the caller
    },
    onError: (err_0: Error) => {
      const message_0 = err_0.message || "Authentication failed";
      // Don't show error for user cancellation
      if (message_0.includes("cancelled") || message_0.includes("canceled") || message_0.includes("abort")) {
        toast.dismiss(toastRef.current!);
      } else {
        toast.error(message_0, {
          id: toastRef.current!
        });
      }
      setError(err_0);
    }
  });

  // Remove passkey mutation
  const removeMutation = useMutation({
    mutationFn: async (credentialId: string) => {
      const response_0 = await fetch(`/api/passkeys/${encodeURIComponent(credentialId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!response_0.ok) {
        const data_5 = await response_0.json();
        throw new Error(data_5.error || "Failed to remove passkey");
      }
    },
    onMutate: () => {
      toastRef.current = toast.loading("Removing passkey...");
    },
    onSuccess: () => {
      toast.success("Passkey removed", {
        id: toastRef.current!
      });
      queryClient.invalidateQueries({
        queryKey: ["passkeys", userId]
      });
    },
    onError: (err_1: Error) => {
      toast.error(err_1.message || "Failed to remove passkey", {
        id: toastRef.current!
      });
      setError(err_1);
    }
  });

  // Wrapper functions
  const dismissPrompt = useCallback(async () => {
    await dismissPromptMutation.mutateAsync();
  }, [dismissPromptMutation]);
  const registerPasskey = useCallback(async (deviceName_0?: string): Promise<boolean> => {
    try {
      await registerMutation.mutateAsync(deviceName_0);
      return true;
    } catch {
      return false;
    }
  }, [registerMutation]);
  const authenticateWithPasskey = useCallback(async () => {
    try {
      const result_0 = await authenticateMutation.mutateAsync();

      // Complete the session by verifying the OTP with Supabase
      if (result_0.email && result_0.token) {
        const {
          error: verifyError
        } = await supabaseClient.auth.verifyOtp({
          email: result_0.email,
          token: result_0.token,
          type: "magiclink"
        });
        if (verifyError) {
          console.error("Failed to verify OTP:", verifyError);
          return {
            success: false
          };
        }
      }
      return {
        success: true,
        redirectUrl: result_0.redirectUrl
      };
    } catch {
      return {
        success: false
      };
    }
  }, [authenticateMutation]);
  const removePasskey = useCallback(async (credentialId_0: string) => {
    await removeMutation.mutateAsync(credentialId_0);
  }, [removeMutation]);
  return {
    // Passkey data
    passkeys,
    hasPasskeys,
    isLoading,
    // Biometric info
    biometricType,
    biometricDisplayName: getBiometricDisplayName(biometricType),
    // null = not yet checked, true = supported, false = not supported
    // For rendering: show UI unless explicitly false
    isPasskeySupported: isSupported !== false,
    supportDetails,
    // Prompt logic
    shouldShowPrompt,
    dismissPrompt,
    // Registration
    registerPasskey,
    isRegistering: registerMutation.isLoading,
    // Authentication
    authenticateWithPasskey,
    isAuthenticating: authenticateMutation.isLoading,
    // Management
    removePasskey,
    isRemoving: removeMutation.isLoading,
    // Errors
    error
  };
}

/**
 * Hook specifically for login page (doesn't require authentication)
 */
export function usePasskeyLogin() {
  // Start with null to match server render, then update after mount
  // This prevents hydration mismatch while still showing button quickly
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [supportDetails, setSupportDetails] = useState<PasskeySupportResult | null>(null);
  const [biometricType, setBiometricType] = useState<BiometricType>("passkey");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const toastRef = useRef<string | number | null>(null);

  // After mount: immediately set optimistic value, then run full async check
  useEffect(() => {
    // Immediate optimistic check (sync) - shows button right after hydration
    const optimistic = canProbablyUsePasskeys();
    setIsSupported(optimistic);
    setBiometricType(getBiometricType());

    // Full async check - only updates if platform authenticator unavailable
    const runCheck = async () => {
      const result = await checkPasskeySupport();
      if (result.supported !== optimistic) {
        setIsSupported(result.supported);
      }
      setSupportDetails(result);
    };
    runCheck();
  }, []);
  const authenticateWithPasskey = useCallback(async (): Promise<{
    success: boolean;
    redirectUrl?: string;
  }> => {
    setIsAuthenticating(true);
    setError(null);
    toastRef.current = toast.loading("Authenticating...");
    try {
      // 1. Get authentication options from server
      const optionsResponse = await fetch("/api/passkeys/login-options", {
        method: "POST",
        credentials: "include"
      });
      if (!optionsResponse.ok) {
        const data = await optionsResponse.json();
        throw new Error(data.error || "Failed to get authentication options");
      }
      const options = await optionsResponse.json();

      // 2. Start WebAuthn authentication
      const credential = await startAuthentication({
        optionsJSON: options
      });

      // 3. Verify with server
      const verifyResponse = await fetch("/api/passkeys/login-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          credential
        })
      });
      if (!verifyResponse.ok) {
        const data_0 = await verifyResponse.json();
        throw new Error(data_0.error || "Failed to verify authentication");
      }
      const result_0 = await verifyResponse.json();

      // 4. Complete the session by verifying the OTP with Supabase
      if (result_0.email && result_0.token) {
        const {
          error: verifyError
        } = await supabaseClient.auth.verifyOtp({
          email: result_0.email,
          token: result_0.token,
          type: "magiclink"
        });
        if (verifyError) {
          throw new Error("Failed to complete login session");
        }
      }
      toast.success("Login successful!", {
        id: toastRef.current!
      });
      return {
        success: true,
        redirectUrl: result_0.redirectUrl
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed";

      // Don't show error for user cancellation
      if (message.includes("cancelled") || message.includes("canceled") || message.includes("abort")) {
        toast.dismiss(toastRef.current!);
      } else {
        toast.error(message, {
          id: toastRef.current!
        });
        setError(err instanceof Error ? err : new Error(message));
      }
      return {
        success: false
      };
    } finally {
      setIsAuthenticating(false);
    }
  }, []);
  return {
    // null = not yet checked, true = supported, false = not supported
    // For rendering: show button unless explicitly false
    isPasskeySupported: isSupported !== false,
    // True only after we've confirmed support (for enabling functionality)
    isReady: isSupported === true,
    supportDetails,
    biometricType,
    biometricDisplayName: getBiometricDisplayName(biometricType),
    authenticateWithPasskey,
    isAuthenticating,
    error
  };
}